import React from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';

import fetchSalon from 'actions/fetchSalon.action';
import Button from 'components/form/Button.component';
import ExternalLink from 'components/generic/ExternalLink.component';
import S from 'constants/string.const';
import { withIsFromMobileApp } from 'hooks/useIsFromMobileApp.hook';
import { withGilt, withTranslation } from 'providers/GILT.provider';
import { withTheme } from 'providers/Theme.provider';

import PopupModalContainer from 'common/PopupModal';
import { bookingLogCSV, clearAllErrorMessages, deletedBookingLogCSV, transactionLogCSV } from 'reports/actions';
import ClientsLayout from 'reports/DownloadableReports/clients/mappers/ClientsLayoutContainer';
import PayoutLog from 'reports/DownloadableReports/PayoutLog.component';

import CN from './DownloadableReportsLayout.module.scss';

const HELP_LINK = 'https://help.getslick.com/en/articles/3514031-sms-marketing-overview';

class DownloadableReportsLayout extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: { currentlyLoading: false, loaded: true },
    };
  }

  UNSAFE_componentWillMount() {
    this.props.fetchSalon(this.props.match.params.id);
  }

  // noinspection JSCheckFunctionSignatures
  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.match.params.id !== nextProps.match.params.id) {
      this.props.fetchSalon(nextProps.match.params.id);
      this.props.history.push(`/salon/${nextProps.match.params.id}/reports/main`);
    }
  }

  onDownloadBtnClicked = (type) => {
    if (this.props.lastDatesSelected) {
      switch (type) {
        case 'Transaction': {
          this.props.transactionLogCSV(this.props.match.params.id, this.props.lastDatesSelected.fromDate, this.props.lastDatesSelected.toDate);
          break;
        }
        case 'Booking': {
          this.props.bookingLogCSV(this.props.match.params.id, this.props.lastDatesSelected.fromDate, this.props.lastDatesSelected.toDate);
          break;
        }

        case 'Deleted': {
          this.props.deletedBookingLogCSV(this.props.match.params.id, this.props.lastDatesSelected.fromDate, this.props.lastDatesSelected.toDate);
          break;
        }
        default: {
          break;
        }
      }
    }
  };

  onGoButtonClicked = (fromDate, toDate) => {
    this.setState({ loading: { currentlyLoading: false, loaded: true, dateRange: { from: fromDate, to: toDate } } });
  };

  getErrorToShow = () => {
    if (this.props.transactionLogProgress.errorMessage) return this.props.transactionLogProgress.errorMessage;
    if (this.props.bookingLogProgress.errorMessage) return this.props.bookingLogProgress.errorMessage;
    if (this.props.deletedBookingLogProgress.errorMessage) return this.props.deletedBookingLogProgress.errorMessage;
    return null;
  };

  hideError = () => {
    this.props.clearAllErrorMessages();
  };

  render() {
    const { isDarkMode, lastDatesSelected, t, formatDate, isSalonInGB } = this.props;

    const fromDate = lastDatesSelected ? formatDate(lastDatesSelected.fromDate, { hideTime: true, hideWeekday: true }) : '';
    const toDate = lastDatesSelected ? formatDate(lastDatesSelected.toDate, { hideTime: true, hideWeekday: true }) : '';

    const transactionTitle = !lastDatesSelected ? t('businessReports.downloadableReports.transactionLog') : t('businessReports.downloadableReports.transactionLogDateRange', { fromDate, toDate });

    const bookingTitle = !lastDatesSelected ? t('businessReports.downloadableReports.bookingLog') : t('businessReports.downloadableReports.bookingLogDateRange', { fromDate, toDate });

    const deletedBookingTitle = !lastDatesSelected
      ? t('businessReports.downloadableReports.deletedBookingLog')
      : t('businessReports.downloadableReports.deletedBookingLogDateRange', { fromDate, toDate });

    const errorToShow = this.getErrorToShow();

    return (
      <div
        data-bem="DownloadableReportsLayout"
        className={cn({
          [CN.component]: true,
          [CN.dark]: isDarkMode,
        })}
      >
        {errorToShow && <PopupModalContainer popupTitle={t('common.error')} popupMessage={errorToShow} buttonText={t('common.buttons.okay')} buttonCallback={this.hideError} />}
        <div className="has-protipbox download">
          <div className={CN.container}>
            <div className={CN.panel}>
              <div className={CN.panelCol}>
                <div className={CN.panelRow}>
                  {S.lamp}
                  <div className={CN.panelText}>
                    <div className={CN.panelTitle}>{t('businessReports.downloadableReports.proTipTitle')}</div>
                    {t('businessReports.downloadableReports.proTip1.part1')}
                    {S.space}
                    {isSalonInGB && <span>{t('businessReports.downloadableReports.proTip1.part2')}</span>}
                  </div>
                </div>
                <div className={CN.panelRow}>
                  {S.lamp}
                  <div className={CN.panelText}>
                    <div className={CN.panelTitle}>{t('businessReports.downloadableReports.proTipTitle')}</div>
                    {t('businessReports.downloadableReports.proTip2')}
                  </div>
                </div>
                <div className={CN.panelRow}>
                  {S.lamp}
                  <div className={CN.panelText}>
                    <div className={CN.panelTitle}>{t('businessReports.downloadableReports.splitTakingsLogTitle')}</div>
                    {t('businessReports.downloadableReports.splitTakingsLogDescription')}
                  </div>
                </div>
              </div>
              {!this.props.isFromMobile && (
                <div className={CN.linkContainer}>
                  <ExternalLink link={HELP_LINK} variant="link">
                    <div className={CN.link}>{t('businessReports.downloadableReports.needHelp')}</div>
                  </ExternalLink>
                </div>
              )}
            </div>

            <ClientsLayout />
            <div className={CN.subTitle}>{t('businessReports.downloadableReports.downloadableReportsTitle')}</div>
            <div className={CN.downloadContainer}>
              {transactionTitle}
              {this.state.loading.loaded && (
                <Button
                  onClick={() => {
                    this.onDownloadBtnClicked('Transaction');
                  }}
                  variant="primary"
                  disabled={this.props.transactionLogProgress.isFetching}
                >
                  {t('businessReports.downloadableReports.downloadTransactionLogButton')}
                </Button>
              )}
            </div>

            <div className={CN.downloadContainer}>
              {bookingTitle}
              {this.state.loading.loaded && (
                <Button
                  variant="primary"
                  onClick={() => {
                    this.onDownloadBtnClicked('Booking');
                  }}
                  disabled={this.props.bookingLogProgress.isFetching}
                >
                  {t('businessReports.downloadableReports.downloadBookingLogButton')}
                </Button>
              )}
            </div>

            <div className={CN.downloadContainer}>
              {deletedBookingTitle}
              {this.state.loading.loaded && (
                <Button
                  variant="primary"
                  onClick={() => {
                    this.onDownloadBtnClicked('Deleted');
                  }}
                  disabled={this.props.deletedBookingLogProgress.isFetching}
                >
                  {t('businessReports.downloadableReports.downloadDeletedBookingLogButton')}
                </Button>
              )}
            </div>

            <PayoutLog bgn={fromDate} end={toDate} />
          </div>
        </div>
      </div>
    );
  }
}

export function mapStateToProps(state) {
  const { reports } = state;
  const { transactionLog, bookingLog, deletedBookingLog, lastDatesSelected } = reports;
  return {
    transactionLogProgress: { isFetching: transactionLog.isFetching, errorMessage: transactionLog.errorMessage },
    bookingLogProgress: { isFetching: bookingLog.isFetching, errorMessage: bookingLog.errorMessage },
    deletedBookingLogProgress: {
      isFetching: deletedBookingLog.isFetching,
      errorMessage: deletedBookingLog.errorMessage,
    },
    lastDatesSelected,
  };
}

export function mapDispatchToProps(dispatch) {
  return {
    transactionLogCSV(salonId, startDate, endDate) {
      dispatch(transactionLogCSV(salonId, startDate, endDate));
    },
    bookingLogCSV(salonId, startDate, endDate) {
      dispatch(bookingLogCSV(salonId, startDate, endDate));
    },
    deletedBookingLogCSV(salonId, startDate, endDate) {
      dispatch(deletedBookingLogCSV(salonId, startDate, endDate));
    },
    fetchSalon(salonId) {
      dispatch(fetchSalon(salonId));
    },
    clearAllErrorMessages() {
      dispatch(clearAllErrorMessages());
    },
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(withIsFromMobileApp(withTheme()(withGilt(withTranslation()(DownloadableReportsLayout)))));
